import { Link } from 'react-router-dom';
import type { DashboardStats } from '@/lib/api/dashboard';

interface JobsPipelineProps {
  stats: DashboardStats;
}

interface Stage {
  label: string;
  count: number;
  color: string;
  bg: string;
  icon: string;
}

export const JobsPipeline = ({ stats }: JobsPipelineProps) => {
  const stages: Stage[] = [
    { label: 'Quoted', count: stats.pendingQuotes + stats.approvedQuotes, color: 'text-primary', bg: 'bg-primary', icon: 'request_quote' },
    { label: 'Scheduled', count: stats.scheduledJobs, color: 'text-warning', bg: 'bg-warning', icon: 'event' },
    { label: 'In Progress', count: stats.inProgressJobs, color: 'text-blue-600', bg: 'bg-blue-500', icon: 'construction' },
    { label: 'Completed', count: stats.completedJobs, color: 'text-success', bg: 'bg-success', icon: 'task_alt' },
  ];

  const total = stages.reduce((s, st) => s + st.count, 0) || 1;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-bold text-gray-900">Jobs Pipeline</h3>
        <Link to="/jobs" className="text-xs font-bold text-primary hover:underline">View all</Link>
      </div>

      {/* Stacked progress bar */}
      <div className="flex h-2.5 rounded-full overflow-hidden gap-0.5">
        {stages.map((st) =>
          st.count > 0 ? (
            <div
              key={st.label}
              className={`${st.bg} transition-all`}
              style={{ width: `${(st.count / total) * 100}%` }}
              title={`${st.label}: ${st.count}`}
            />
          ) : null
        )}
      </div>

      {/* Stage rows */}
      <div className="space-y-3">
        {stages.map((st) => (
          <div key={st.label} className="flex items-center gap-3">
            <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${st.bg}/10`}>
              <span className={`material-symbols-outlined text-sm leading-none ${st.color}`}>{st.icon}</span>
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-0.5">
                <span className="text-xs font-medium text-gray-700">{st.label}</span>
                <span className="text-xs font-bold text-gray-900">{st.count}</span>
              </div>
              <div className="h-1 rounded-full bg-gray-100 overflow-hidden">
                <div
                  className={`h-full ${st.bg} rounded-full transition-all`}
                  style={{ width: `${(st.count / total) * 100}%` }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="pt-1 border-t border-gray-100 flex justify-between text-xs text-gray-500">
        <span>{total} total jobs</span>
        <span className="font-medium text-primary">
          {stats.pipelineValue > 0
            ? `$${(stats.pipelineValue / 1000).toFixed(0)}k pipeline`
            : 'No pipeline value'}
        </span>
      </div>
    </div>
  );
};
